import { Box, Button, Grid, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import InputCustom from '../../components/atoms/input/InputCustom'
import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import styles from './membersProfile.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { updateVendorStaffProfile } from '../../features/friendSlice'
import { getStaffInfo } from '../../features/vendorSlice'
import TimeOffTable from '../../components/time-management/TimeOffTable'
import TaskTimelineTable from '../../components/time-management/TaskTimelineTable'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { toastHelper } from '../../util/toastHelper'
import { validateCanadianPhone, validatePassword } from '../../util/passwordValidation'

function CustomTabPanel(props) {
	const { children, value, index, ...other } = props

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box>{children}</Box>}
		</div>
	)
}

export const UserProfileTab = ({ data }) => {
	const [value, setValue] = useState(0)
	
	// Time off related state
	const [search, setSearch] = useState('')

	const handleChange = (event, newValue) => {
		setValue(newValue)
	}

	const handleExportClick = async () => {
		try {
			let url, csvHeaders, fileName
			
			if (value === 1) {
				// Time Off tab - export time-off requests for this staff member
				url = `${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/time-off-request-list?page=1&limit=1000`
				csvHeaders = ['Date', 'Time Off Type', 'Reason For Time Off', 'Status']
				const staffName = `${data.firstName || ''}-${data.lastName || ''}`.replace(/\s+/g, '-').toLowerCase()
				fileName = `${staffName}-time-off-requests-${dayjs().format('YYYY-MM-DD')}.csv`
			} else if (value === 2) {
				// Task Timeline tab - export task timeline for this staff member
				url = `${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/time-entry-list?page=1&limit=1000`
				csvHeaders = ['Date', 'Wedding', 'Task', 'Hours']
				const staffName = `${data.firstName || ''}-${data.lastName || ''}`.replace(/\s+/g, '-').toLowerCase()
				fileName = `${staffName}-task-timeline-${dayjs().format('YYYY-MM-DD')}.csv`
			}
			
			// Add staffId parameter to get this specific staff member's data
			if (data._id) {
				url += `&staffId=${data._id}`
			}
			
			if (search && search.trim()) {
				url += `&search=${encodeURIComponent(search.trim())}`
			}

			const response = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			const result = await response.json()
			
			if (!result.success) {
				toast.error('Failed to fetch data for export')
				return
			}

			// Prepare CSV data based on tab
			let csvData
			if (value === 1) {
				// Time Off data
				csvData = result.data.map((item) => [
					dayjs(item.date).format('YYYY-MM-DD'),
					item.timeOffType,
					`"${item.reason.replace(/"/g, '""')}"`, // Escape quotes in reason
					item.status.charAt(0).toUpperCase() + item.status.slice(1)
				])
			} else if (value === 2) {
				// Task Timeline data
				csvData = result.data.map((item) => [
					dayjs(item.date).format('YYYY-MM-DD'),
					item.kazooId?.kazooName || 'N/A',
					`"${(item.taskDetails || 'N/A').replace(/"/g, '""')}"`, // Escape quotes in task
					item.staffId?.firstName ? `${item.staffId.firstName} ${item.staffId.lastName || ''}`.trim() : 'N/A',
					item.hour || '0.0'
				])
			}

			// Create CSV content
			const csvContent = [
				csvHeaders.join(','),
				...csvData.map(row => row.join(','))
			].join('\n')

			// Create and download CSV file
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
			const link = document.createElement('a')
			
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob)
				link.setAttribute('href', url)
				link.setAttribute('download', fileName)
				link.style.visibility = 'hidden'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
			}
			
			toast.success(`CSV exported successfully! (${result.data.length} records)`)
			
		} catch (error) {
			console.error('Error exporting CSV:', error)
			toast.error('Failed to export CSV. Please try again.')
		}
	}

	const mainTabStyle = {
		'&.MuiTabs-root': {
			minHeight: 'unset',
		},
		'& .MuiButtonBase-root': {
			padding: '11px 16px',
			minHeight: 'unset',
			fontSize: '14px',
			fontFamily: 'Inter',
			fontWeight: '500',
			color: '#313131',
			textTransform: 'capitalize',
			'&.Mui-selected': {
				color: '#7CA6BD',

				'& svg path': {
					stroke: '#7CA6BD',
				},
			},
		},
		'& .MuiTabs-indicator': {
			backgroundColor: '#7CA6BD',
		},
		'@media (max-width: 599px)': {
			'& .MuiButtonBase-root': {
				padding: '8px',
			},
		},
	}
	const innerTabStyle = {
		'&.MuiTabs-root': {
			minHeight: 'unset',
			backgroundColor: '#F2F6F8',
			padding: '2px 4px',
			borderRadius: '10px',
		},
		'& .MuiTabs-scroller': {
			padding: '2px 0',
		},

		'& .MuiButtonBase-root': {
			padding: '8px',
			minHeight: 'unset',
			fontSize: '14px',
			fontFamily: 'Inter',
			fontWeight: '500',
			color: '#8692A4',
			textTransform: 'capitalize',
			minWidth: ' auto !important',

			'&.Mui-selected': {
				color: '#7CA6BD',
				backgroundColor: '#fff',
				borderRadius: '8px',
				boxShadow: '0px 1px 3px 0px #0000001A',
				minWidth: ' auto !important',

				'& svg path': {
					stroke: '#7CA6BD',
				},
			},
		},
		'& .MuiTabs-indicator': {
			backgroundColor: 'transparent',
		},
		'@media (max-width: 599px)': {
			'& .MuiButtonBase-root': {
				padding: '8px',
			},
		},
	}

	// ----- for vendor when viewing staff -----
	const dispatch = useDispatch()

	const staff = useSelector((state) => state.vendor.staff)
	const accountType = useSelector((state) => state.auth.accountType)
	// const needApprovalTasks = useSelector((state) => state.task.tasks)

	const [save, setSave] = useState(false)
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		phone: '',
		password:''
	})

	const isAccountStaff = useMemo(() => {
		if (!data?._id || !staff?.length) return false
		return staff.some((s) => s.staffId === data._id)
	}, [data?._id, staff])

	const handleSubmit = () => {
		if (save) {
			if (!formData.firstName?.trim())
				return toastHelper('First name is required', 'error')
			if (!formData.lastName?.trim())
				return toastHelper('Last name is required', 'error')
			if (formData.phone && !validateCanadianPhone(formData.phone))
				return toastHelper(
					'Phone number must be in valid format',
					'error'
				)
			if (!!formData.password?.trim()) {
				const error = validatePassword(formData.password)
				if (error) return toastHelper(error, 'error')
			}
			dispatch(
				updateVendorStaffProfile({
					staffId: data._id,
					updates: formData,
				})
			)
			setFormData({ ...formData, password: '' })
		}
		setSave((prev) => !prev)
	}

	const handleFormChange = (e) => {
		const { name, value } = e.target
		const val =
			name === 'phone'
				? value.replace(/[^0-9]/g, '').slice(0, 10)
				: value.replace(/[^a-zA-Z]/g, '')
		setFormData({ ...formData, [name]: val?.trim() })
	}

	useEffect(() => {
		dispatch(getStaffInfo())
		// dispatch(getWaitingForApprovalTasksByAssignee(data._id))
		setFormData({
			firstName: data.firstName || '',
			lastName: data.lastName || '',
			phone: data.phone || '',
			password:''
		})
	}, [dispatch, data?._id, accountType])

	return (
		<>
			<Box sx={{ width: '100%' }} className={styles.membersProfileTabs}>
				<Box sx={{ borderBottom: 1, borderColor: '#DFE4EB' }}>
					<Tabs
						value={value}
						onChange={handleChange}
						aria-label="basic tabs example"
						sx={mainTabStyle}
					>
						<Tab
							label={
								<div className={styles.tabItem}>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className="tab-icon"
									>
										<path
											d="M8.10573 7.24634C8.03906 7.23967 7.95906 7.23967 7.88573 7.24634C6.29906 7.19301 5.03906 5.89301 5.03906 4.29301C5.03906 2.65967 6.35906 1.33301 7.99906 1.33301C9.6324 1.33301 10.9591 2.65967 10.9591 4.29301C10.9524 5.89301 9.6924 7.19301 8.10573 7.24634Z"
											stroke="#313131"
											stroke-width="1.5"
											stroke-miterlimit="10"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M4.7725 9.70699C3.15917 10.787 3.15917 12.547 4.7725 13.6203C6.60583 14.847 9.6125 14.847 11.4458 13.6203C13.0592 12.5403 13.0592 10.7803 11.4458 9.70699C9.61917 8.48699 6.6125 8.48699 4.7725 9.70699Z"
											stroke="#313131"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>

									<span>Profile Details</span>
								</div>
							}
						/>
						{isAccountStaff && (<Tab
							label={
								<div className={styles.tabItem}>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className="tab-icon"
									>
										<path
											d="M5.33398 1.33301V3.33301"
											stroke="#313131"
											stroke-width="1.5"
											stroke-miterlimit="10"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M10.666 1.33301V3.33301"
											stroke="#313131"
											stroke-width="1.5"
											stroke-miterlimit="10"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M2.33398 6.05957H13.6673"
											stroke="#313131"
											stroke-width="1.5"
											stroke-miterlimit="10"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M14 5.66634V11.333C14 13.333 13 14.6663 10.6667 14.6663H5.33333C3 14.6663 2 13.333 2 11.333V5.66634C2 3.66634 3 2.33301 5.33333 2.33301H10.6667C13 2.33301 14 3.66634 14 5.66634Z"
											stroke="#313131"
											stroke-width="1.5"
											stroke-miterlimit="10"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M10.4625 9.13314H10.4685"
											stroke="#313131"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M10.4625 11.1331H10.4685"
											stroke="#313131"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M7.99569 9.13314H8.00168"
											stroke="#313131"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M7.99569 11.1331H8.00168"
											stroke="#313131"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M5.53084 9.13314H5.53683"
											stroke="#313131"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M5.53084 11.1331H5.53683"
											stroke="#313131"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>

									<span>Time Off</span>
								</div>
							}
						/>)}
						{isAccountStaff && (<Tab
							label={
								<div className={styles.tabItem}>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className="tab-icon"
									>
										<path
											d="M8.24707 5.91992H11.7471"
											stroke="#313131"
											stroke-width="1.5"
											stroke-miterlimit="10"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M4.25293 5.91992L4.75293 6.41992L6.25293 4.91992"
											stroke="#313131"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M8.24707 10.5869H11.7471"
											stroke="#313131"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M4.25293 10.5869L4.75293 11.0869L6.25293 9.58691"
											stroke="#313131"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M5.99967 14.6663H9.99967C13.333 14.6663 14.6663 13.333 14.6663 9.99967V5.99967C14.6663 2.66634 13.333 1.33301 9.99967 1.33301H5.99967C2.66634 1.33301 1.33301 2.66634 1.33301 5.99967V9.99967C1.33301 13.333 2.66634 14.6663 5.99967 14.6663Z"
											stroke="#313131"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>

									<span>Task Timeline</span>
								</div>
							}
						/>)}
					</Tabs>
				</Box>
				<div className={styles.tabContentWrp}>
					<CustomTabPanel
						value={value}
						index={0}
						className={styles.profileDetailsWrp}
					>
						<div className={styles.titleRow}>
							<Typography variant="h3">
								Profile Details
							</Typography>
							{isAccountStaff && (
								<div
									style={{
										display: 'flex',
									}}
								>
									{save ? (
										<div style={{display: 'flex',gap: '10px'}}>
											<ButtonCustom
												onClick={handleSubmit}
											>
												Save
											</ButtonCustom>
											<ButtonCustom
												onClick={() => {
													setSave((prev) => !prev)
												}}
											>
												Cancel
											</ButtonCustom>
										</div>
									) : (
										<img
                                            data-cy="edit-profile"
											src="/images/edit-icon.svg"
											alt=""
											className={styles.editIcon}
											onClick={() =>
												setSave((prev) => !prev)
											}
										/>
									)}
								</div>
							)}
						</div>
						<div className={styles.detailInfo}>
							<div className={styles.infoItem}>
								<label htmlFor="firstName">First Name</label>
								<input
									type="text"
									id="firstName"
									name="firstName"
									value={formData.firstName}
									readOnly={!save}
									onChange={handleFormChange}
								/>
							</div>
							<div className={styles.infoItem}>
								<label htmlFor="lastName">Last Name</label>
								<input
									type="text"
									id="lastName"
									name="lastName"
									value={formData.lastName}
									readOnly={!save}
									onChange={handleFormChange}
								/>
							</div>
							{data.__t === 'user' && (
								<div className={styles.infoItem}>
									<label htmlFor="preferredName">
										Preferred Name
									</label>
									<input
										type="text"
										id="preferredName"
										name="preferredName"
										value={data.preferredName}
										readOnly={!save}
									/>
								</div>
							)}
							<div className={styles.infoItem}>
								<label htmlFor="email">Email</label>
								<input
									type="email"
									id="email"
									name="email"
									value={data.email}
									readOnly
								/>
							</div>
							<div className={styles.infoItem}>
								<label htmlFor="phone">Phone Number</label>
								<input
									type="number"
									id="phone"
									name="phone"
									value={formData.phone}
									readOnly={!save}
									onChange={handleFormChange}
								/>
							</div>
							{data.__t === 'vendor-staff' && (
								<div className={styles.infoItem}>
									<label htmlFor="Role">Role</label>
									<input
										type="text"
										id="Role"
										name="Role"
										value="Team Member"
										readOnly
									/>
								</div>
							)}
							{save && (
								<div
									className={`${styles.infoItem} ${styles.formGroup}`}
								>
									<label htmlFor="password">Password</label>
									<InputCustom
										data-cy="password"
										placeholder="Password"
										name="password"
										type="password"
										enableShowPassword={true}
										value={formData.password}
										setDataFunction={setFormData}
										data={formData}
										required={true}
										eyeIcon={true}
										autoComplete="new-password"
									/>
								</div>
							)}
						</div>
					</CustomTabPanel>
					<CustomTabPanel
						value={value}
						index={1}
						className={styles.profileDetailsWrp}
					>
						<div className={styles.titleRow}>
							<Typography variant="h3">
								Time Off Requests
							</Typography>
						</div>
						<Box className={styles.searchRow}>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, sm: 4, md: 2.4 }}>
									<div className={styles.searchContainer}>
										<InputCustom
											type="text"
											name="search"
											id="search"
											placeholder="Search"
											error={false}
											searchIcon={true}
											value={search || ''}
											onChange={(e) =>
												setSearch(e.target.value)
											}
										/>
									</div>
								</Grid>
								<Grid size={{ xs: 12, sm: 8, md: 9.6 }}>
									<div className={styles.right}>
										<ButtonCustom
											variant="outlined"
											size="small"
											onClick={handleExportClick}
											style={{ minHeight: '40px' }}
										>
											<img src="/images/export.svg" />
											<Typography variant="body">
												Export
											</Typography>
										</ButtonCustom>
									</div>
								</Grid>
							</Grid>
						</Box>
						<TimeOffTable searchTerm={search} staffId={data._id} />
					</CustomTabPanel>
					<CustomTabPanel
						value={value}
						index={2}
						className={styles.profileDetailsWrp}
					>
						<div className={styles.titleRow}>
							<Typography variant="h3">
								Task Timeline
							</Typography>
						</div>
						<Box className={styles.searchRow}>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, sm: 4, md: 2.4 }}>
									<div className={styles.searchContainer}>
										<InputCustom
											type="text"
											name="search"
											id="search"
											placeholder="Search"
											error={false}
											searchIcon={true}
											value={search || ''}
											onChange={(e) =>
												setSearch(e.target.value)
											}
										/>
									</div>
								</Grid>
								<Grid size={{ xs: 12, sm: 8, md: 9.6 }}>
									<div className={styles.right}>
										<ButtonCustom
											variant="outlined"
											size="small"
											onClick={handleExportClick}
											style={{ minHeight: '40px' }}
										>
											<img src="/images/export.svg" />
											<Typography variant="body">
												Export
											</Typography>
										</ButtonCustom>
									</div>
								</Grid>
							</Grid>
						</Box>
						<TaskTimelineTable searchTerm={search} staffId={data._id} />
					</CustomTabPanel>
				</div>
			</Box>
		</>
	)
}
